import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Video,
  Phone,
  CheckCircle2,
  Mail,
} from "lucide-react";

const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

// IMPORTANT: Replace this with your actual Calendly URL
const CALENDLY_URL = "https://calendly.com/bonza-business-and-franchise-sales/sell-my-ecommerce-business";

const BookingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    // Track page view for booking
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Book Appointment',
        page_location: window.location.href
      });
    }

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Listen for Calendly events
  useEffect(() => {
    const handleCalendlyEvent = (e) => {
      if (e.data.event === "calendly.event_scheduled") {
        // Track appointment booked
        if (window.gtag) {
          window.gtag('event', 'appointment_booked', {
            appointment_type: 'broker_call',
            booking_source: 'calendly',
            page_url: window.location.href
          });
        }
      }
    };

    window.addEventListener("message", handleCalendlyEvent);
    return () => window.removeEventListener("message", handleCalendlyEvent);
  }, []);

  const handlePhoneClick = () => {
    if (window.trackPhoneClick) {
      window.trackPhoneClick("1300 266 922");
    }
  };

  const handleEmailClick = () => {
    if (window.trackEmailClick) {
      window.trackEmailClick("sales@bonzabfs.com.au");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Info */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Book a Confidential Broker Call
                </h1>
                <p className="text-slate-600 mb-8">
                  Schedule a free, no-obligation call with an experienced ecommerce 
                  business broker to discuss your business and explore your options.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">30 Minutes</h3>
                      <p className="text-sm text-slate-500">Focused discussion about your business</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Video or Phone Call</h3>
                      <p className="text-sm text-slate-500">Your choice of meeting format</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Confidential & Free</h3>
                      <p className="text-sm text-slate-500">No obligation, no pressure</p>
                    </div>
                  </div>
                </div>

                <Card className="bg-gradient-to-br from-blue-600 to-green-500 text-white">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-3">What We'll Discuss</h3>
                    <ul className="space-y-2 text-sm text-blue-100">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Your business overview and goals
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Current market conditions
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Valuation considerations
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Sale process and timeline
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Your questions answered
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Alternative Contact */}
                <div className="mt-8 p-6 bg-white rounded-xl shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-4">Prefer to Contact Us Directly?</h3>
                  <div className="space-y-3">
                    <a
                      href="tel:1300266922"
                      onClick={handlePhoneClick}
                      className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      <span>1300 266 922</span>
                    </a>
                    <a
                      href="mailto:sales@bonzabfs.com.au"
                      onClick={handleEmailClick}
                      className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <Mail className="w-5 h-5" />
                      <span>sales@bonzabfs.com.au</span>
                    </a>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/contact")}
                    className="w-full mt-4"
                  >
                    Send an Enquiry Instead
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column - Calendly Widget */}
            <div className="lg:col-span-2">
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div
                    className="calendly-inline-widget"
                    data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=2563eb`}
                    style={{ minWidth: "320px", height: "700px" }}
                  />
                </CardContent>
              </Card>
              
              <p className="text-center text-sm text-slate-500 mt-4">
                By booking a call, you agree to our{" "}
                <Link to="/privacy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </Link>
                . All discussions are confidential.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-8 brightness-0 invert" />
              <span className="text-sm">Powered by Bonza Business & Franchise Sales</span>
            </div>
            <img src={BONZA_LOGO} alt="Bonza" className="h-6" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;
